import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  Check, 
  X, 
  Upload, 
  Loader2,
  Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// Helper to create image from URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

// Helper to get cropped image as blob
async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  // Create cover image (max 1200px width, maintaining aspect ratio)
  const maxWidth = 1200;
  const scale = Math.min(1, maxWidth / pixelCrop.width);
  const coverCanvas = document.createElement('canvas');
  const coverCtx = coverCanvas.getContext('2d');
  coverCanvas.width = Math.round(pixelCrop.width * scale);
  coverCanvas.height = Math.round(pixelCrop.height * scale);
  coverCtx.drawImage(canvas, 0, 0, pixelCrop.width, pixelCrop.height, 0, 0, coverCanvas.width, coverCanvas.height);

  return new Promise((resolve) => {
    coverCanvas.toBlob(
      (blob) => {
        resolve(blob);
      },
      'image/jpeg',
      0.85
    );
  });
}

/**
 * CoverImageUploader - Component for selecting and cropping cover images
 * 
 * @param {string} currentCoverUrl - URL of current cover image
 * @param {function} onCoverChange - Callback when cover changes (receives URL or blob depending on mode)
 * @param {boolean} deferUpload - If true, returns blob instead of uploading (for form submissions)
 * @param {function} onBlobReady - Callback when blob is ready (only used when deferUpload=true)
 * @param {string} className - Additional CSS classes
 */
export default function CoverImageUploader({ 
  currentCoverUrl,
  onCoverChange,
  deferUpload = false,
  onBlobReady,
  className = ''
}) {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Store the callback ref to avoid closure issues
  const onCoverChangeRef = useRef(onCoverChange);
  onCoverChangeRef.current = onCoverChange;

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande (máx 10MB)');
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result);
      setDialogOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
    });
    reader.readAsDataURL(file);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      console.log('Missing data:', { imageSrc: !!imageSrc, croppedAreaPixels: !!croppedAreaPixels });
      return;
    }

    setUploading(true);
    try {
      // Get cropped image
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      console.log('Cropped blob size:', croppedBlob?.size);

      // If deferUpload is true, just return the blob and preview URL
      if (deferUpload) {
        // Create a local URL for preview
        const previewUrl = URL.createObjectURL(croppedBlob);
        
        // Notify parent with preview URL for display
        if (onCoverChangeRef.current) {
          onCoverChangeRef.current(previewUrl);
        }
        
        // Also provide the blob for later upload
        if (onBlobReady) {
          onBlobReady(croppedBlob);
        }
        
        setDialogOpen(false);
        setImageSrc(null);
        toast.success('Imagem selecionada!');
        return;
      }

      // Upload via backend (immediate upload mode)
      const uploadFormData = new FormData();
      uploadFormData.append('file', croppedBlob, 'cover.jpg');

      const response = await api.post('/storage/upload-image-direct', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const { file_url } = response.data;
      console.log('Upload successful, file_url:', file_url);

      // Notify parent BEFORE closing dialog to ensure state updates
      if (onCoverChangeRef.current && file_url) {
        console.log('Calling onCoverChange with:', file_url);
        onCoverChangeRef.current(file_url);
      }

      // Small delay to ensure state propagates, then close
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setDialogOpen(false);
      setImageSrc(null);
      toast.success('Imagem carregada com sucesso!');

    } catch (error) {
      console.error('Error uploading cover:', error);
      toast.error('Erro ao salvar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setImageSrc(null);
  };

  const resetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  return (
    <>
      {/* Upload trigger area */}
      <div className={className}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="cover-file-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full border-dashed border-2 h-20 flex flex-col items-center justify-center gap-2 hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors"
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              <span className="text-sm text-slate-500">{deferUpload ? 'Processando...' : 'Fazendo upload...'}</span>
            </>
          ) : (
            <>
              <Upload className="w-6 h-6 text-orange-500" />
              <span className="text-sm text-slate-500">Clique para {deferUpload ? 'selecionar' : 'fazer upload de'} uma imagem</span>
            </>
          )}
        </Button>
      </div>

      {/* Crop Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Ajustar Imagem de Capa</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Crop area - 16:9 aspect ratio for cover */}
            <div className="relative h-64 sm:h-72 bg-slate-900 rounded-xl overflow-hidden">
              {imageSrc && (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={16 / 9}
                  showGrid={true}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              )}
            </div>

            {/* Zoom control */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">Zoom</span>
                <Button variant="ghost" size="sm" onClick={resetCrop}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Resetar
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <ZoomOut className="w-4 h-4 text-slate-400" />
                <Slider
                  value={[zoom]}
                  min={1}
                  max={3}
                  step={0.1}
                  onValueChange={([value]) => setZoom(value)}
                  className="flex-1"
                />
                <ZoomIn className="w-4 h-4 text-slate-400" />
              </div>
            </div>

            {/* Preview hint */}
            <p className="text-xs text-center text-slate-500">
              Arraste para posicionar • Use o slider para ajustar o zoom
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-3">
            <Button variant="outline" onClick={handleCancel} disabled={uploading}>
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={uploading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {deferUpload ? 'Processando...' : 'Salvando...'}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Usar esta imagem
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
